<template>
  <v-dialog v-model="isOpen" max-width="450px" @update:modelValue="onDialogToggle">
    <v-card>
      <v-card-title>Add SMS — {{ company?.name }}</v-card-title>

      <!-- Result dialog — sits on top of the form -->
      <v-dialog v-model="resultDialog.show" max-width="420px">
        <v-card>
          <v-card-title>{{ resultDialog.success ? 'SMS Provisioned' : 'Provisioning Failed' }}</v-card-title>
          <v-card-text style="white-space: pre-line;">{{ resultDialog.message }}</v-card-text>
          <v-card-actions>
            <v-spacer></v-spacer>
            <v-btn text color="primary" @click="onResultOk">OK</v-btn>
          </v-card-actions>
        </v-card>
      </v-dialog>

      <v-card-text>
        <p class="mb-3">
          Provision a new SMS-capable Twilio phone number for
          <strong>{{ company?.name }}</strong>.
        </p>

        <!-- Area Code -->
        <v-text-field
          v-model="form.areaCode"
          label="Area Code *"
          maxlength="3"
          :error-messages="errors.areaCode"
          :class="{ 'shake-animation': shakes.areaCode }"
          @input="onAreaCodeInput"
        ></v-text-field>
      </v-card-text>

      <v-card-actions>
        <v-spacer></v-spacer>
        <v-btn text @click="cancel" :disabled="submitting">Cancel</v-btn>
        <v-btn text color="primary" @click="submit" :loading="submitting">Add</v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script>
import axios from 'axios';

export default {
  name: 'OwnerAccountAddSMS',

  props: {
    modelValue: {
      type: Boolean,
      required: true
    },
    company: {
      type: Object,
      default: null
    }
  },

  emits: ['update:modelValue', 'saved'],

  data() {
    return {
      form: this.freshForm(),
      errors: { areaCode: '' },
      shakes: { areaCode: false },
      submitting: false,
      savedCompany: null,
      resultDialog: {
        show: false,
        success: false,
        message: ''
      }
    };
  },

  computed: {
    isOpen: {
      get() { return this.modelValue; },
      set(val) { this.$emit('update:modelValue', val); }
    }
  },

  methods: {
    freshForm() {
      return {
        areaCode: '425'
      };
    },

    onDialogToggle(val) {
      if (!val) this.resetForm();
    },

    resetForm() {
      this.form = this.freshForm();
      this.errors = { areaCode: '' };
      this.shakes = { areaCode: false };
      this.submitting = false;
      this.savedCompany = null;
    },

    cancel() {
      this.resetForm();
      this.isOpen = false;
    },

    // Called when user clicks OK on the result dialog
    onResultOk() {
      this.resultDialog.show = false;
      if (this.resultDialog.success) {
        this.$emit('saved', this.savedCompany);
        this.resetForm();
        this.isOpen = false;
      }
      // Failure path: just dismiss the result dialog, leave Add form open
    },

    onAreaCodeInput() {
      this.form.areaCode = this.form.areaCode.replace(/\D/g, '');
      this.errors.areaCode = '';
    },

    triggerShake(field) {
      this.shakes[field] = true;
      setTimeout(() => { this.shakes[field] = false; }, 500);
    },

    // Format an E.164 US number (+12065551234) as 206-555-1234
    formatPhone(e164) {
      if (!e164) return '';
      const digits = e164.replace(/\D/g, '');
      const ten = digits.startsWith('1') ? digits.slice(1) : digits;
      if (ten.length !== 10) return e164;
      return `${ten.slice(0, 3)}-${ten.slice(3, 6)}-${ten.slice(6)}`;
    },

    validate() {
      let valid = true;
      if (!this.form.areaCode || this.form.areaCode.length !== 3) {
        this.errors.areaCode = 'Area code must be 3 digits';
        this.triggerShake('areaCode');
        valid = false;
      }
      return valid;
    },

    async submit() {
      if (!this.validate()) return;
      if (!this.company?._id) return;

      this.submitting = true;
      try {
        const response = await axios.post(
          `${process.env.VUE_APP_THIS_SERVER}/portaldb/db/companies/${this.company._id}/sms`,
          { areaCode: this.form.areaCode }
        );

        if (!response.data.success) {
          this.resultDialog = {
            show: true,
            success: false,
            message: response.data.message || 'Provisioning failed.'
          };
          return;
        }

        this.savedCompany = response.data.company;
        const formatted = this.formatPhone(this.savedCompany.smsPhoneNumber);
        this.resultDialog = {
          show: true,
          success: true,
          message: `Be sure to add ${formatted} to the messaging service! Do it right now!`
        };
      } catch (error) {
        console.error('Error adding SMS:', error);
        this.resultDialog = {
          show: true,
          success: false,
          message: error.response?.data?.message || error.message || 'An unexpected error occurred.'
        };
      } finally {
        this.submitting = false;
      }
    }
  }
};
</script>

<style scoped>
.shake-animation {
  animation: shake 0.5s cubic-bezier(.36,.07,.19,.97) both;
}

@keyframes shake {
  10%, 90% { transform: translate3d(-1px, 0, 0); }
  20%, 80% { transform: translate3d(2px, 0, 0); }
  30%, 50%, 70% { transform: translate3d(-4px, 0, 0); }
  40%, 60% { transform: translate3d(4px, 0, 0); }
}
</style>
